"use client";

/**
 * Site-wide motion runtime. Mounted once in the root layout.
 *
 * Responsibilities:
 * - Initialise Lenis smooth scroll with the Venetian ground-truth feel:
 *   duration 1.4, expo-out easing, no touch smoothing.
 * - Drive Lenis from the GSAP ticker and forward its scroll events to
 *   ScrollTrigger so scrubbed triggers stay in lockstep with the smoothed
 *   scroll position.
 * - Refresh ScrollTrigger once web fonts resolve (Fraunces reflows
 *   headings, which moves every trigger start position).
 * - Do nothing at all under prefers-reduced-motion: native scroll, no
 *   ticker work, and the individual motion components each bail out on
 *   the same query, leaving static visible content.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import { gsap, ScrollTrigger, prefersReducedMotion } from "./gsap";

export function MotionProvider({ children }: { children: ReactNode }) {
  const lenisRef = useRef<Lenis | null>(null);
  const pathname = usePathname();

  useEffect(() => {
    const reduceQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    let lenis: Lenis | null = null;
    let rafCallback: ((time: number) => void) | null = null;

    const start = () => {
      if (lenis) return;
      lenis = new Lenis({
        duration: 1.4,
        // Expo-out, exactly as shipped on venetianspa.ca.
        easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        // Lenis 1.x replaced `smoothTouch: false` with `syncTouch: false`
        // (native touch scrolling, no interception). Same intent.
        syncTouch: false,
      });
      lenisRef.current = lenis;
      lenis.on("scroll", ScrollTrigger.update);
      rafCallback = (time: number) => {
        lenis?.raf(time * 1000);
      };
      gsap.ticker.add(rafCallback);
      gsap.ticker.lagSmoothing(0);
    };

    const stop = () => {
      if (rafCallback) gsap.ticker.remove(rafCallback);
      rafCallback = null;
      lenis?.destroy();
      lenis = null;
      lenisRef.current = null;
    };

    if (!prefersReducedMotion()) start();

    // Follow live preference changes (e.g. user toggles the OS setting
    // mid-session) rather than only sampling at mount.
    const onPreferenceChange = (event: MediaQueryListEvent) => {
      if (event.matches) stop();
      else start();
    };
    reduceQuery.addEventListener("change", onPreferenceChange);

    // Fraunces and Instrument Sans swap in after first paint and change
    // heading metrics; recompute every trigger position once they settle.
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled) ScrollTrigger.refresh();
    });

    return () => {
      cancelled = true;
      reduceQuery.removeEventListener("change", onPreferenceChange);
      stop();
    };
  }, []);

  // Lenis keeps its own smoothed scroll position across App Router
  // navigations, so a footer link could land the new page at the old
  // page's offset. Jump to the top on every pathname change, except when
  // the URL targets an in-page anchor (e.g. /aftercare#injectables).
  useEffect(() => {
    if (window.location.hash) return;
    lenisRef.current?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname]);

  return <>{children}</>;
}
